import { useEffect, useMemo } from "react";
import { Box, Button, TextField, Typography, CircularProgress } from "@mui/material";
import { BaseModal } from "../../components/Modal/BaseModal";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchInventoryDetail,
  selectInventoryDetail,
  clearInventoryDetail,
} from "../../slices/inventorymanagement.slice";
import {
  Grid,
  GridColumn as Column,
  GridDetailRowProps,
} from "@progress/kendo-react-grid";
import { GridColumnProps } from "@progress/kendo-react-grid";
import { ExpandableGrid } from "../../components/DataTable/ExpandableGrid";
import "@progress/kendo-theme-default/dist/all.css";
import styles from '../../components/DataTable/DataTable.module.scss';
import styled from 'styled-components';

interface Props {
  open: boolean;
  handleClose: () => void;
  productId: number;
  productName: string;
  unit: string;
}

const StyledGrid = styled(Grid)`
  .k-column-title {
    min-width: 0;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 1;
    overflow: hidden;
    font-weight: bold;
  }
`;

// Helper function to format numbers to 2 decimal places
const formatNumber = (value: any): string => {
  if (value === null || value === undefined || value === '') return '0.00';
  const num = parseFloat(value);
  return isNaN(num) ? '0.00' : num.toFixed(2);
};

const ConsumptionDetailComponent = (props: GridDetailRowProps) => {
  const dataItem = props.dataItem;

  return (
    <section style={{
      padding: '10px',
      backgroundColor: '#f9f9f9',
      maxHeight: '200px', // Reduced height
      overflow: 'auto',
      position: 'relative' // Ensure proper positioning
    }}>
      <h4 style={{ marginBottom: '10px', color: '#333', fontSize: '14px' }}>Consumption Details</h4>
      {dataItem.consumptions && dataItem.consumptions.length > 0 ? (
        <div style={{
          maxHeight: '150px', // Constrain inner content
          overflow: 'auto'
        }}>
          <StyledGrid
            data={dataItem.consumptions}
            style={{ border: "1px solid #ddd" }}
            scrollable="none" // Disable inner grid scrolling to prevent conflicts
          >
            <Column
              field="appliedDate"
              title="Applied Date"
              className={styles.headerColumn}
            />
            <Column
              field="fieldName"
              title="Field Applied"
              className={styles.headerColumn}
            />
            <Column
              field="quantityApplied"
              title="Quantity Applied"
              className={styles.headerColumn}
              cells={{
                data: (props) => (
                  <td>{formatNumber(props.dataItem.quantityApplied)}</td>
                )
              }}
            />
          </StyledGrid>
        </div>
      ) : (
        <div style={{
          padding: '15px',
          textAlign: 'center',
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography variant="body2" color="textSecondary">
            No applications from this purchase batch yet.
          </Typography>
        </div>
      )}
    </section>
  );
};

export const InventoryDetailModal = ({ open, handleClose, productId, productName, unit }: Props) => {
  const dispatch = useAppDispatch();
  const inventoryDetail = useAppSelector(selectInventoryDetail);
  const isLoading = useAppSelector((state) => state.inventorymanagement.loadingDetail);

  const inventorySummary = useAppSelector((state) => state.inventorymanagement.inventorySummary);
  const selectedSummaryItem = inventorySummary.find((item: any) => item.productId === productId);

  useEffect(() => {
    if (open && productId) {
      dispatch(fetchInventoryDetail(productId));
    }
    return () => {
      dispatch(clearInventoryDetail());
    };
  }, [dispatch, open, productId]);

  const purchaseColumns: GridColumnProps[] = useMemo(() => [
    {
      field: "purchaseDate",
      title: "Purchase Date",
      width: "120px",
    },
    {
      field: "openingStock",
      title: "Opening Stock",
      width: "120px",
      cells: {
        data: (props) => (
          <td>{formatNumber(props.dataItem.openingStock)}</td>
        )
      }
    },
    {
      field: "quantityPurchased",
      title: "Qty Purchased",
      width: "120px",
      cells: {
        data: (props) => (
          <td>{formatNumber(props.dataItem.quantityPurchased)}</td>
        )
      }
    },
    {
      field: "unitPrice",
      title: "Unit Price",
      width: "100px",
      cells: {
        data: (props) => (
          <td>${formatNumber(props.dataItem.unitPrice)}</td>
        )
      }
    },
    {
      field: "closingStock",
      title: "Closing Stock",
      width: "120px",
      cells: {
        data: (props) => (
          <td>{formatNumber(props.dataItem.closingStock)}</td>
        )
      }
    },
    {
      field: "invoiceRef",
      title: "Invoice Ref",
      width: "150px",
    },
  ], []);

  return (
    <BaseModal open={open} handleClose={handleClose}>
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={3}
        sx={{
          maxHeight: "80vh",
          overflow: "hidden"
        }}
      >
        {/* Header */}
        <Box sx={{ flexShrink: 0 }}>
          <Typography variant="h6">View Inventory - {productName}</Typography>
        </Box>

        {/* Summary Fields */}
        <Box
          display={"flex"}
          flexWrap={"wrap"}
          gap={2}
          sx={{ flexShrink: 0 }}
        >
          <TextField
            label="Product Name"
            variant="outlined"
            value={productName}
            InputProps={{ readOnly: true }}
            sx={{ minWidth: "150px" }}
          />
          <TextField
            label="Unit"
            variant="outlined"
            value={unit}
            InputProps={{ readOnly: true }}
            sx={{ maxWidth: "100px" }}
          />
          <TextField
            label="Total Purchased"
            variant="outlined"
            value={formatNumber(selectedSummaryItem?.totalPurchased || inventoryDetail?.totalPurchased || 0)}
            InputProps={{ readOnly: true }}
            sx={{ maxWidth: "120px" }}
          />
          <TextField
            label="Total Applied"
            variant="outlined"
            value={formatNumber(selectedSummaryItem?.totalApplied || inventoryDetail?.totalApplied || 0)}
            InputProps={{ readOnly: true }}
            sx={{ maxWidth: "120px" }}
          />
          <TextField
            label="Current Stock"
            variant="outlined"
            value={formatNumber(selectedSummaryItem?.currentStock || inventoryDetail?.currentStock || 0)}
            InputProps={{ readOnly: true }}
            sx={{maxWidth: "120px"}}
          />
        </Box>

        {/* Grid Section - Allow natural scrolling */}
        <Box sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0
        }}>
          <Box sx={{ flexShrink: 0, mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Purchase & Application History
            </Typography>
          </Box>

          {/* Expandable Grid Container - Remove height constraints */}
          <Box sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            paddingBottom: '5px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: isLoading ? 'center' : 'flex-start'
          }}>
            {isLoading ? (
              <CircularProgress sx={{ color: 'text.secondary' }}/>
            ) : (
              <ExpandableGrid
                data={inventoryDetail?.transactions || []}
                dataItemKey="id"
                columns={purchaseColumns}
                detailComponent={ConsumptionDetailComponent}
              />
            )}
          </Box>
        </Box>

        {/* Footer Buttons - Absolute positioning to stay visible */}
        <Box
  display={"flex"}
  justifyContent={"flex-end"}
  gap={2}
  sx={{
    position: 'sticky',
    bottom: 10,
    backgroundColor: 'white',
    borderTop: '1px solid #e0e0e0',
    padding: 2,
    zIndex: 10
  }}
>
  <Button variant="outlined" onClick={handleClose}>
    Close
  </Button>
</Box>
      </Box>
    </BaseModal>
  );
};